import { cx } from '@emotion/css';
import { Button } from '@sortlist-frontend/design-system/legacy';
import { PropsWithChildren } from 'react';

import * as S from './styles';

type Cta = {
  label: string | React.ReactElement;
  link?: string;
  onClick?: () => void;
};

export type SectionType = {
  name: string;
  description?: string | React.ReactNode;
  title: string | React.ReactNode;
  titleStyle?: string;
  cta?: Cta;
  illustration?: string;
  inverted?: boolean;
  imageAlt?: string;
  index?: number;
  showBackground?: boolean;
  isCropped?: boolean;
  content?: React.ReactNode;
  fullWidthContent?: boolean;
  width?: number;
  height?: number;
};

export default function Section(props: PropsWithChildren<SectionType>) {
  const {
    cta,
    description,
    illustration,
    imageAlt,
    inverted = false,
    name,
    showBackground = false,
    title,
    titleStyle,
    children,
    isCropped,
    content,
    fullWidthContent = false,
    width,
    height,
  } = props;

  const getContainer = () => {
    if (fullWidthContent) return;
    if (content != null || cta != null) return 'container-md';
    return 'container-lg';
  };

  const handleOnClick = () => {
    if (cta == null) return;
    if (cta.link != null) return (window.location.href = cta.link);
    if (cta.onClick != null) return cta.onClick();
  };

  return (
    <section data-testid={name} className={cx(showBackground ? 'bg-secondary-50' : 'bg-neutral-50', 'overflow-hidden')}>
      <div
        className={cx(
          getContainer(),
          'gasm:p-16 layout-column layout-gt-sm-row layout-align-start-center flex-1 max-sm:my-16 my-24',
        )}>
        <div
          className={cx(
            inverted ? 'flex-order-1' : 'flex-order-gt-sm-0',
            illustration != null ? 'flex-gt-sm-50 layout-align-center-start' : 'layout-align-center-center',
            'flex-order-xs-1 flex-100 layout-column',
          )}>
          <p className={cx(illustration == null && 'text-center', titleStyle, 'h1-gt-xs h3-xs lh-1 bold max-sm:mt-10')}>
            {title}
          </p>
          {description != null && (
            <p className={cx(illustration == null && 'text-center', 'max-sm:mt-8 mt-4 h6 lh-3 text-secondary-700')}>
              {description}
            </p>
          )}
          {cta != null && (
            <Button
              className="mt-8"
              label={cta.label}
              buttonVariant="raised"
              buttonStyle="primary"
              size="lg"
              onClick={handleOnClick}
            />
          )}
          {content != null && content}
          {children}
        </div>
        {illustration != null && imageAlt != null && (
          <div
            className={cx(
              inverted
                ? 'flex-order-gt-xs-0 layout-align-gt-xs-center-end'
                : 'flex-order-gt-xs-1 layout-align-gt-xs-center-start',
              !isCropped && 'layout-align-center-center',
              'flex-order-xs-0 flex-gt-sm-50 flex-100 layout-column relative',
            )}>
            <S.SectionImage
              isCropped={!!isCropped}
              src={illustration}
              width={width}
              height={height}
              alt={imageAlt}
              quality={100}
              className="width-xs-100"
            />
          </div>
        )}
      </div>
    </section>
  );
}
